import React, { useEffect, useState } from "react";
import { marketingApi } from "../marketingApi";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Lock, AlertTriangle, Clock, Activity, Shield } from "lucide-react";
import BudgetPanel from "./BudgetPanel";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const STATUS_META = {
  executed:              { color: "emerald", icon: CheckCircle2, label: "Ejecutada" },
  prepared_not_executed: { color: "sky",     icon: Lock,         label: "Preparada · no ejecutada" },
  dry_run_ok:            { color: "indigo",  icon: Activity,     label: "Dry-run" },
  failed:                { color: "red",     icon: XCircle,      label: "Fallida" },
  rejected:              { color: "slate",   icon: XCircle,      label: "Rechazada" },
  rolled_back:           { color: "amber",   icon: Clock,        label: "Rollback" },
};

export default function AuditTab() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [entries, setEntries] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [limits, setLimits] = useState(null);

  const load = async () => {
    try {
      const [a, al, li] = await Promise.all([
        marketingApi.director.auditList(),
        marketingApi.director.alertsList(),
        marketingApi.director.limits(),
      ]);
      setEntries(a.audit || []);
      setAlerts(al.alerts || []);
      setLimits(li);
    } catch { setEntries([]); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="audit-tab">
      {/* Budget / Token metering panel — admin edits, marketing views */}
      <BudgetPanel isAdmin={isAdmin} />
      {/* Limits card */}
      {limits && (
        <section className="rounded-2xl border border-[#CCE2F8] bg-white p-5" aria-label="Límites operativos">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={16} className="text-[#0A2A5E]" />
            <h3 className="text-sm font-semibold text-[#0A2A5E]">Límites operativos activos</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="rounded-lg bg-[#F3F8FE] p-3">
              <div className="text-slate-500">Escrituras diarias máx.</div>
              <div className="text-xl font-bold text-slate-800 tabular-nums mt-0.5">{limits.max_daily_writes}</div>
            </div>
            <div className="rounded-lg bg-[#F3F8FE] p-3">
              <div className="text-slate-500">Δ presupuesto diario máx.</div>
              <div className="text-xl font-bold text-slate-800 tabular-nums mt-0.5">+${limits.max_daily_budget_delta_usd}</div>
            </div>
            <div className="rounded-lg bg-[#F3F8FE] p-3">
              <div className="text-slate-500">Δ presupuesto mensual máx.</div>
              <div className="text-xl font-bold text-slate-800 tabular-nums mt-0.5">+${limits.max_monthly_budget_delta_usd}</div>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">
            TZ por defecto: <b>{limits.default_timezone}</b>. Modifica los límites en Gestionar &gt; Secretos
            (variables `MARKETING_*`). Hard-cap absoluto en código: 200 escrituras/día, +$2.000/día, +$10.000/mes.
          </p>
        </section>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50/60 p-5" aria-label="Alertas de rendimiento">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={16} className="text-amber-700" />
            <h3 className="text-sm font-semibold text-amber-900">Anomalías detectadas (sólo recomendaciones)</h3>
          </div>
          <ul className="space-y-2">
            {alerts.map((a) => (
              <li key={a.id} className="rounded-lg bg-white border border-amber-200 p-3 text-xs" data-testid={`alert-${a.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-amber-900">{a.metric_label}</span>
                  <span className="tabular-nums text-red-600 font-bold">{a.delta_pct?.toFixed(1)}%</span>
                </div>
                <div className="text-slate-600 mt-1">
                  {a.current} vs {a.previous} en periodo {a.period?.current?.start} → {a.period?.current?.end}
                </div>
                <div className="text-slate-500 italic mt-1">💡 {a.suggestion}</div>
                <button
                  onClick={async () => { await marketingApi.director.alertDismiss(a.id); load(); }}
                  className="mt-2 text-[10px] text-slate-500 hover:text-[#0A2A5E] font-semibold"
                  data-testid={`alert-dismiss-${a.id}`}
                >Descartar</button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Audit list */}
      <section className="rounded-2xl border border-[#CCE2F8] bg-white p-5" aria-label="Historial auditable">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-[#0A2A5E]" />
            <h3 className="text-sm font-semibold text-[#0A2A5E]">Auditoría de acciones (sanitizada)</h3>
          </div>
          <button onClick={load} className="text-[11px] text-[#0A2A5E] font-semibold hover:underline" data-testid="audit-refresh">
            Actualizar
          </button>
        </div>
        {entries === null ? (
          <div className="text-xs text-slate-500">Cargando…</div>
        ) : entries.length === 0 ? (
          <div className="text-xs text-slate-500 italic">
            Aún no hay registros. Cada vez que apruebes una propuesta se escribirá aquí (sin tokens).
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="audit-table">
              <thead className="text-left text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Plataforma</th>
                  <th className="py-2 pr-3">Acción</th>
                  <th className="py-2 pr-3">Estado</th>
                  <th className="py-2 pr-3">Dry-run</th>
                  <th className="py-2 pr-3">Graph IDs</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const s = STATUS_META[e.execution_result?.status] || STATUS_META.failed;
                  const Icon = s.icon;
                  return (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`audit-row-${e.id}`}>
                      <td className="py-2 pr-3 tabular-nums text-slate-500">{fmtTime(e.created_at)}</td>
                      <td className="py-2 pr-3"><b className="text-slate-800">{e.platform}</b></td>
                      <td className="py-2 pr-3 font-mono text-slate-600">{e.action}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center gap-1 rounded-full bg-${s.color}-50 text-${s.color}-700 border border-${s.color}-200 px-2 py-0.5 text-[10px] font-semibold`}>
                          <Icon size={10} /> {s.label}
                        </span>
                      </td>
                      <td className="py-2 pr-3">{e.dry_run ? "sí" : "no"}</td>
                      <td className="py-2 pr-3 font-mono text-[10px] text-slate-500">
                        {e.execution_result?.graph_ids
                          ? Object.entries(e.execution_result.graph_ids).map(([k,v]) => `${k}=${v}`).join(" · ")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
